import { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ROUTES } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';

const BASE_ROUTE_TITLE = 'C8 Health';
const PageTitleContext = createContext();

const useRouteTitles = () => {
  const sidebarLang = useSelector(getLang('SIDEBAR'));
  const headerLang = useSelector(getLang('HEADER'));
  const settingLang = useSelector(getLang('SETTINGS'));
  const suggestContentLang = useSelector(getLang('SUGGEST_CONTENT'));
  const qualityMetricsLang = useSelector(getLang('QUALITY_METRIC'));

  const ROUTE_TITLES = new Map([
    [ROUTES.ROOT, sidebarLang.HOME],
    [ROUTES.HOME, sidebarLang.HOME],
    [ROUTES.WELCOME, 'Sign In'], //to check

    // User management
    [ROUTES.USER_PROFILE, headerLang.PROFILE],
    [ROUTES.USERS_MANAGEMENT, headerLang.USERS_MANAGEMENT],

    // Settings
    [ROUTES.SETTINGS, headerLang.SETTINGS],
    [ROUTES.SETTINGS_MAGIC_LINK, settingLang.MAGIC_LINK],
    [ROUTES.SETTINGS_WORKSPACES, settingLang.WORKSPACE_TITLE],
    [ROUTES.SETTINGS_CONTACT_UPLOAD, settingLang.CONTACT_UPLOAD],
    [ROUTES.SETTINGS_ORGANIZATIONAL_STRUCTURE, settingLang.ORG_STRUCTURE_TITLE],
    [ROUTES.SETTINGS_UPLOAD_SHIFTS, settingLang.SHIFTS_UPLOAD],

    // Knowledge & Content
    [ROUTES.KNOWLEDGE, sidebarLang.ROOT],
    [ROUTES.MY_CONTENT, sidebarLang.MY_CONTENT],
    [ROUTES.FAVORITES, sidebarLang.FAVORITES],
    [ROUTES.SEARCH, 'Search'],
    [ROUTES.SUGGEST_CONTENT, suggestContentLang.TITLE],
    [ROUTES.KNOWLEDGE_HUB, sidebarLang.KNOWLEDGE_HUB],

    // Content
    [ROUTES.ADMISSION, sidebarLang.ADMISSION],
    [ROUTES.LABS, sidebarLang.LAB],
    [ROUTES.ROTATIONS, sidebarLang.ROTATIONS],
    [ROUTES.CONTACTS, sidebarLang.CONTACTS],
    [ROUTES.QUALITY_METRIC, qualityMetricsLang.METRICS_HEADER],

    // Communication
    [ROUTES.COMMUNICATION_CENTER, sidebarLang.COMMUNICATION_CENTER],

    // Additional routes
    ['/verification', headerLang.SIGN_IN],
    ['/not-allowed', headerLang.CONTACT_US],
  ]);

  const getPageTitle = pathname => {
    if (!pathname) return null;

    if (ROUTE_TITLES.has(pathname)) {
      return ROUTE_TITLES.get(pathname);
    }

    for (const [route, title] of ROUTE_TITLES.entries()) {
      if (pathname.startsWith(route + '/')) {
        return title;
      }
    }

    return null;
  };
  return getPageTitle;
};

export const PageTitleProvider = ({ children }) => {
  const location = useLocation();
  const [subtitle, setSubtitle] = useState(null);
  const getPageTitle = useRouteTitles();

  const updateSubtitle = newTitle => {
    if (!newTitle) {
      return;
    }
    setSubtitle(newTitle);
  };

  // Update document title when state changes
  useEffect(() => {
    const basePageTitle = getPageTitle(location.pathname);
    const parts = [BASE_ROUTE_TITLE];

    if (basePageTitle) parts.push(basePageTitle);
    if (subtitle) parts.push(subtitle);
    document.title = parts.join(' - ');
  }, [location.pathname, subtitle, getPageTitle]);

  // Reset title when route changes
  useEffect(() => {
    setSubtitle(null);
  }, [location.pathname]);

  return (
    <PageTitleContext.Provider value={{ updateSubtitle }}>{children}</PageTitleContext.Provider>
  );
};

export const usePageTitle = () => {
  const context = useContext(PageTitleContext);
  if (!context) {
    throw new Error('usePageTitle must be used within a PageTitleProvider');
  }
  return context;
};
